import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useAppendMachineMaintenance,
  useMachineDetail,
  useMachineLatestMeters,
  useMachineMaintenance,
  useMachineMeterQuarantine,
  useMachineAudit,
  useMe,
  useMyPermissions,
  useRestartGateway,
} from '../../api/queries';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import { MachineStatusChips } from './MachineStatusChips';
import { MachineVersionsTab, ActiveVersionConfigPanel } from './MachineVersionsTab';
import { MachineDailyMetersTab } from './MachineDailyMetersTab';
import { MachineEventsTab } from './MachineEventsTab';
import { MeterQuarantineDialog } from './MeterQuarantineDialog';

const MAINT_ACTIONS = [
  { value: 'cleanup',           label: 'Cleanup' },
  { value: 'firmware_update',   label: 'Firmware update' },
  { value: 'part_replacement',  label: 'Part replacement' },
  { value: 'inspection',        label: 'Inspection' },
  { value: 'other',             label: 'Other' },
];

export type MachineDetailDialogProps = {
  machineId: string | null;
  onClose: () => void;
  onEdit: (machine: import('../../api/types').MachineDetail) => void;
};

/**
 * Tabbed detail view for a machine — Identity / Configuration / Ownership /
 * Lifecycle / Maintenance log. The maintenance tab is the only one that
 * mutates state; everything else routes through Edit / Set-ownership /
 * Decommission actions on the parent page.
 */
export function MachineDetailDialog({ machineId, onClose, onEdit }: MachineDetailDialogProps) {
  const open = machineId !== null;
  const detail = useMachineDetail(machineId);
  const [tab, setTab] = useState(0);
  const perms = useMyPermissions();
  const has = (code: string) => perms.data?.codes?.includes(code) ?? false;
  const m = detail.data;
  const confirm = useConfirm();
  const alert = useAlert();
  const restartGateway = useRestartGateway(machineId ?? '');

  const onRestartGateway = async () => {
    if (!m) return;
    const ok = await confirm({
      title: 'Restart machine gateway',
      message: <>Force-restart the gateway for <strong>{m.assetNumber}</strong>? This happens
        immediately — it bypasses the normal 10-minute idle wait and interrupts play, so SAS
        polling drops for a few seconds while the container relaunches (any pending gateway
        upgrade is applied on relaunch). In-play credits are unaffected.</>,
      confirmLabel: 'Restart gateway',
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await restartGateway.mutateAsync();
      await alert({ title: 'Restart requested', message: r.message, severity: 'success' });
    } catch (e) {
      await alert({ title: 'Restart failed',
        message: (e as { message?: string }).message ?? 'Could not queue the restart.', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack>
          <Typography variant="h6">{m ? `${m.make} ${m.model}` : 'Machine'}</Typography>
          {m && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {m.assetNumber}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tab label="Identity" />
        <Tab label="Configuration" />
        <Tab label="Meters" />
        <Tab label="Daily meters" />
        <Tab label="Events" />
        <Tab label="Ownership" />
        <Tab label="Lifecycle" />
        <Tab label="Maintenance log" />
        <Tab label="Versions" />
        {has('audit.view') && <Tab label="Audit" />}
      </Tabs>
      <DialogContent dividers sx={{ minHeight: 360 }}>
        {detail.isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} /><Typography>Loading…</Typography>
          </Stack>
        ) : detail.error ? (
          <ErrorAlert>{(detail.error as { message?: string }).message}</ErrorAlert>
        ) : m ? (
          <>
            <MeterQuarantineBanner machineId={m.id} machineLabel={m.assetNumber} />
            {tab === 0 && <IdentityTab m={m} />}
            {tab === 1 && <ConfigTab m={m} canEdit={has('machines.edit')} />}
            {tab === 2 && <MetersTab machineId={m.id} />}
            {tab === 3 && <MachineDailyMetersTab machineId={m.id} machineLabel={m.assetNumber} gameName={m.gameName} />}
            {tab === 4 && <MachineEventsTab machineId={m.id} siteId={m.siteId} />}
            {tab === 5 && <OwnershipTab m={m} />}
            {tab === 6 && <LifecycleTab m={m} />}
            {tab === 7 && (
              <MaintenanceTab
                machineId={m.id}
                canEdit={has('machines.maintenance.edit')}
              />
            )}
            {tab === 8 && <MachineVersionsTab machineId={m.id} />}
            {tab === 9 && has('audit.view') && <AuditTab machineId={m.id} open={open} />}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {has('machines.restart-gateway') && m && (
          <Button color="warning" onClick={onRestartGateway} disabled={restartGateway.isPending}>
            {restartGateway.isPending ? 'Restarting…' : 'Restart gateway'}
          </Button>
        )}
        {has('machines.edit') && m && (
          <Button variant="contained" onClick={() => onEdit(m)}>Edit machine</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function AuditTab({ machineId, open }: { machineId: string; open: boolean }) {
  const audit = useMachineAudit(machineId, open);
  if (audit.isLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={20} /><Typography>Loading…</Typography>
      </Stack>
    );
  }
  if (audit.error) return <ErrorAlert>{(audit.error as { message?: string }).message}</ErrorAlert>;
  const rows = audit.data ?? [];
  if (rows.length === 0) return <Typography color="text.secondary">No audit events for this machine.</Typography>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Detail</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{fmtDateTime(r.occurredAt)}</TableCell>
              <TableCell><code>{r.eventType}</code></TableCell>
              <TableCell>{fmtOrEmDash(r.reason)}</TableCell>
              <TableCell>{fmtOrEmDash(r.actorDisplayName ?? (r.actorType === 'system' ? 'System' : null))}</TableCell>
              <TableCell>
                <Chip size="small" label={r.outcome}
                  color={r.outcome === 'alarm' ? 'error' : r.outcome === 'failure' ? 'warning' : 'default'} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', columnGap: 2, rowGap: 0.5, mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component="div">{value}</Typography>
    </Box>
  );
}

function IdentityTab({ m }: { m: import('../../api/types').MachineDetail }) {
  // Provisioning identity comes from the strongest available signal, not the
  // legacy sticker_serial/hat_serial columns (often unset on commissioned
  // machines): the bound QR pair for the QR code, and the bound ESP32 device's
  // chip id for the HAT.
  const qrCode = m.qrId ?? m.stickerSerial;
  const hat = m.hatSerial ?? m.deviceChipId;
  return (
    <Stack spacing={1}>
      <Row label="Asset number"  value={<code>{m.assetNumber}</code>} />
      <Row label="Make / model"  value={`${m.make} ${m.model}`} />
      <Row label="Operator"      value={fmtOrEmDash(m.operatorName)} />
      <Row label="Site"          value={fmtOrEmDash(m.siteName)} />
      <Row label="QR Code"       value={qrCode ? <code>{qrCode}</code> : '— (not yet provisioned)'} />
      <Row label="HAT serial"    value={hat ? <code>{hat}</code> : '— (not yet provisioned)'} />
      {/* Two-Status Reporting — ESP32 link + machine-SAS chips. */}
      <Row label="Connectivity"  value={<MachineStatusChips machine={m} />} />
      <Row label="ESP32 online since" value={fmtDateTime(m.esp32OnlineAt)} />
      <Row label="SAS online since"   value={fmtDateTime(m.sasOnlineAt)} />
    </Stack>
  );
}

function ConfigTab({ m, canEdit }: { m: import('../../api/types').MachineDetail; canEdit: boolean }) {
  // Game configuration is per game-version (financial-meters model). This tab
  // shows the ACTIVE version's config and — with machines.edit — lets you edit
  // it and clear its "check details" review flag. Protocol / cashless / firmware
  // are machine-level and stay below.
  return (
    <Stack spacing={2}>
      <Typography variant="overline" color="text.secondary">Game configuration (active version)</Typography>
      <ActiveVersionConfigPanel machineId={m.id} canEdit={canEdit} />
      <Divider />
      <Typography variant="overline" color="text.secondary">Machine configuration</Typography>
      <Stack spacing={1}>
        <Row label="Protocol"      value={<code>{m.protocol}</code>} />
        <Row label="Cashless mode" value={<code>{m.cashlessMode}</code>} />
        <Row label="Firmware"      value={fmtOrEmDash(m.currentFirmwareVersion)} />
      </Stack>
    </Stack>
  );
}

function OwnershipTab({ m }: { m: import('../../api/types').MachineDetail }) {
  const kindLabel =
    m.ownershipKind === 'operator'   ? 'Operator-owned' :
    m.ownershipKind === 'supplier'   ? 'Supplier-owned' :
    m.ownershipKind === 'site_owner' ? 'Site-owner-owned' :
    m.ownershipKind;
  return (
    <Stack spacing={1}>
      <Row label="Ownership kind" value={<Chip size="small" label={kindLabel} />} />
      <Row label="Owner supplier" value={m.ownerSupplierName ?? '—'} />
      <Row label="Owner user (site owner)" value={m.ownerSiteOwnerName ?? '—'} />
      <Row label="Last changed"   value={fmtDateTime(m.ownershipChangedAt)} />
      <Row label="Changed by"     value={m.ownershipChangedByName ?? '—'} />
    </Stack>
  );
}

function LifecycleTab({ m }: { m: import('../../api/types').MachineDetail }) {
  return (
    <Stack spacing={1}>
      <Row label="Status"           value={<Chip size="small" label={m.status} color={
        m.status === 'active' ? 'success' :
        m.status === 'decommissioned' ? 'default' : 'default'} />} />
      <Row label="Decommissioned"   value={fmtDateTime(m.decommissionedAt)} />
      <Row label="Decommission reason" value={fmtOrEmDash(m.decommissionReason)} />
      <Row label="Predecessor"      value={m.predecessorAssetNumber ?? '— (no predecessor on file)'} />
      <Divider sx={{ my: 1 }} />
      <Row label="Created"          value={fmtDateTime(m.createdAt)} />
      <Row label="Created by"       value={m.createdByName ?? '—'} />
      <Row label="Updated"          value={fmtDateTime(m.updatedAt)} />
      <Row label="Updated by"       value={m.updatedByName ?? '—'} />
    </Stack>
  );
}

/** camelCase meter key → a readable label, e.g. "bills1DollarAccepted" → "Bills 1 dollar accepted". */
function prettyMeter(key: string): string {
  const spaced = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9]+)/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The last reported SC→RM meter packet — a raw table of every meter figure the
 * machine reported, plus the packet metadata. Latest values only (no history).
 */
function MetersTab({ machineId }: { machineId: string }) {
  const q = useMachineLatestMeters(machineId);

  if (q.isLoading) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={20} /><Typography>Loading…</Typography>
      </Stack>
    );
  }

  const status = (q.error as { status?: number } | null)?.status;
  if (status === 404) {
    return <Typography color="text.secondary">No meters reported yet.</Typography>;
  }
  if (q.error) {
    return <ErrorAlert>{(q.error as { message?: string }).message}</ErrorAlert>;
  }

  const s = q.data!;
  const entries = Object.entries(s.meters);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center">
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          Last reported packet
        </Typography>
        <Button
          size="small"
          startIcon={q.isFetching ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          onClick={() => q.refetch()}
          disabled={q.isFetching}
        >
          Refresh
        </Button>
      </Stack>

      <Stack spacing={0.5}>
        <Row label="Last reported" value={fmtDateTime(s.receivedAt)} />
        <Row label="Sequence"      value={<code>{s.seq}</code>} />
        <Row
          label="SC clock (UTC)"
          value={s.scTimeUnix ? fmtDateTime(new Date(s.scTimeUnix * 1000).toISOString()) : '—'}
        />
        <Row label="Meter schema"  value={s.meterSchema} />
      </Stack>

      <Divider />

      <TableContainer>
        <Table size="small" aria-label="latest reported meters">
          <TableHead>
            <TableRow>
              <TableCell>Meter</TableCell>
              <TableCell align="right">Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key} hover>
                <TableCell>{prettyMeter(key)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {value.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function MaintenanceTab({ machineId, canEdit }: { machineId: string; canEdit: boolean }) {
  const list = useMachineMaintenance(machineId);
  const append = useAppendMachineMaintenance(machineId);
  const me = useMe();
  const [showForm, setShowForm] = useState(false);
  const [action, setAction] = useState('cleanup');
  const [notes, setNotes] = useState('');

  const onSubmit = () => {
    if (!me.data?.user_id) return;
    append.mutate({
      technicianUserId: me.data.user_id,
      action,
      notes: notes.trim() || null,
    }, {
      onSuccess: () => {
        setShowForm(false);
        setAction('cleanup');
        setNotes('');
      },
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center">
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          Entries
        </Typography>
        {canEdit && !showForm && (
          <Button size="small" variant="outlined" onClick={() => setShowForm(true)}>
            + Append entry
          </Button>
        )}
      </Stack>

      {showForm && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack spacing={2}>
            <TextField
              select
              label="Action"
              size="small"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              {MAINT_ACTIONS.map((a) => (
                <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Notes"
              size="small"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {append.error && (
              <ErrorAlert>{(append.error as { message?: string }).message}</ErrorAlert>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setShowForm(false)} disabled={append.isPending}>Cancel</Button>
              <Button variant="contained" onClick={onSubmit} disabled={append.isPending}>
                {append.isPending ? 'Saving…' : 'Append'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {list.isLoading ? (
        <Stack direction="row" spacing={2}><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
      ) : list.error ? (
        <ErrorAlert>{(list.error as { message?: string }).message}</ErrorAlert>
      ) : (list.data?.length ?? 0) === 0 ? (
        <Typography color="text.secondary">No entries yet.</Typography>
      ) : (
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {list.data!.map((e) => (
            <Box key={e.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip size="small" label={e.action} />
                <Typography variant="body2">{fmtDateTime(e.performedAt)}</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  by {e.technicianUserId.slice(0, 8)}…
                </Typography>
              </Stack>
              {e.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, whiteSpace: 'pre-wrap' }}>
                  {e.notes}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Red banner shown across every tab of the machine detail while the meter
 * engine holds this machine QUARANTINED — incoming readings are recorded but
 * not counted into revenue until an ARMS reviewer resolves the event (owner
 * ruling 2026-08-21, OP1-SITE1-MAC1 RAM-reset incident).
 */
function MeterQuarantineBanner({ machineId, machineLabel }: { machineId: string; machineLabel: string }) {
  const q = useMachineMeterQuarantine(machineId);
  const [reviewOpen, setReviewOpen] = useState(false);
  if (!q.data?.quarantined) return null;
  return (
    <>
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={(
          <Button color="inherit" size="small" onClick={() => setReviewOpen(true)}>
            Review &amp; resolve
          </Button>
        )}
      >
        Meters quarantined since {fmtDateTime(q.data.since ?? null)} — readings are recorded but
        NOT counted into revenue until the event is reviewed and resolved.
      </Alert>
      {reviewOpen && (
        <MeterQuarantineDialog
          open
          onClose={() => setReviewOpen(false)}
          machineId={machineId}
          machineLabel={machineLabel}
        />
      )}
    </>
  );
}
